import { Account } from '@betterfunnel/types';
import { DialogTitle } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import { useContext, useEffect, useMemo, useState } from 'react';
import Autocomplete from '../components/Common/Autocomplete';
import Modal, { ModalActionData } from '../components/Common/Modal';
import { UserContext } from '../contexts/UserContext';
import { classNames } from '../utils';

interface Props {
  open: boolean;
  onClose: () => void;
  onSelect: (account: QueryDocumentSnapshot<Account, DocumentData>) => void;
}

export default function JumpToAccountModal({ open, onClose, onSelect }: Props) {
  const { accounts } = useContext(UserContext);
  const [selected, setSelected] =
    useState<QueryDocumentSnapshot<Account, DocumentData>>();

  useEffect(() => {
    if (open) return;

    setSelected(undefined);
  }, [open]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        onClick: () => onClose(),
      },
      {
        title: 'Select',
        type: 'primary',
        disabled: !selected,
        onClick: () => selected && onSelect?.(selected),
      },
    ],
    [selected],
  );

  return (
    <Modal open={open} onClose={onClose} actions={actions}>
      <DialogTitle
        as="h3"
        className="text-base font-semibold leading-6 text-gray-900"
      >
        Switch account
      </DialogTitle>
      <div className="mt-4 flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Autocomplete<QueryDocumentSnapshot<Account, DocumentData>>
            selected={selected}
            onChange={(selected) => {
              setSelected(selected || undefined);
            }}
            getOptionId={(t) => t.data().slug}
            getValue={(t) => t.data().name}
            renderOption={(option, { active, selected }) => (
              <>
                <div className="flex">
                  <span
                    className={classNames(
                      'truncate',
                      selected && 'font-semibold',
                    )}
                  >
                    {option.data().name}
                  </span>
                  <span
                    className={classNames(
                      'ml-2 truncate text-gray-500',
                      active ? 'text-indigo-200' : 'text-gray-500',
                    )}
                  >
                    {option.data().slug}
                  </span>
                </div>
                {selected && (
                  <span
                    className={classNames(
                      'absolute inset-y-0 right-0 flex items-center pr-4',
                      active ? 'text-white' : 'text-indigo-600',
                    )}
                  >
                    <CheckIcon className="h-5 w-5" aria-hidden="true" />
                  </span>
                )}
              </>
            )}
            options={accounts || []}
            filter={(query, options) =>
              options.filter(
                (option) =>
                  option
                    .data()
                    .name.toLowerCase()
                    .includes(query.toLowerCase()) ||
                  option
                    .data()
                    .slug.toLowerCase()
                    .includes(query.toLowerCase()),
              )
            }
          />
        </div>
      </div>
    </Modal>
  );
}
